import React, { useState, useEffect } from "react";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import { UiModal } from "../../../../Components/Shared/UiModal";

const ModalBankAccount = ({ show, close, settingData, handleSave }) => {
  const [formData, setFormData] = useState({
    bankName: "",
    bankName_ur: "",
    accountHolderName: "",
    accountHolderName_ur: "",
    accountNumber: "",
    iban: "",
    branchCode: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (settingData) {
      setFormData(settingData);
    } else {
      setFormData({
        bankName: "",
        bankName_ur: "",
        accountHolderName: "",
        accountHolderName_ur: "",
        accountNumber: "",
        iban: "",
        branchCode: "",
      });
      setErrors({});
    }
  }, [settingData, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value;

    // Apply specific validation rules for each field
    switch (name) {
      case "bankName":
      case "accountHolderName":
        // Only allow alphabets and spaces
        filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
        break;
      case "accountNumber":
      case "branchCode":
        // Only allow numbers
        filteredValue = value.replace(/[^0-9]/g, '');
        break;
      case "iban":
        // Allow alphanumeric characters (no special characters)
        filteredValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        break;
      default:
        break;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: filteredValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Bank Name validation
    if (!formData.bankName.trim()) {
      newErrors.bankName = "Bank name is required";
    } else if (formData.bankName.trim().length < 2) {
      newErrors.bankName = "Bank name must be at least 2 characters";
    }

    // Account Holder Name validation
    if (!formData.accountHolderName.trim()) {
      newErrors.accountHolderName = "Account holder name is required";
    } else if (formData.accountHolderName.trim().length < 2) {
      newErrors.accountHolderName = "Account holder name must be at least 2 characters";
    }

    // Account Number validation
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = "Account number is required";
    } else if (formData.accountNumber.length < 5) {
      newErrors.accountNumber = "Account number must be at least 5 digits";
    }

    // IBAN validation
    if (!formData.iban.trim()) {
      newErrors.iban = "IBAN is required";
    } else if (formData.iban.length < 8) {
      newErrors.iban = "IBAN must be at least 8 characters";
    }

    // Branch Code validation
    if (!formData.branchCode.trim()) {
      newErrors.branchCode = "Branch code is required";
    } else if (formData.branchCode.length < 3) {
      newErrors.branchCode = "Branch code must be at least 3 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (validateForm()) {
      handleSave(formData);
    }
  };

  // Check if all fields are valid
  const isFormValid =
    formData.bankName.trim().length >= 2 &&
    formData.accountHolderName.trim().length >= 2 &&
    formData.accountNumber.length >= 5 &&
    formData.iban.length >= 8 &&
    formData.branchCode.length >= 3;

  const isEditMode = Boolean(settingData?.id);
  const fieldClass = (name) =>
    `ui-input-field ${errors[name] ? "border-brand-error" : ""}`;

  return (
    <UiModal
      isOpen={show}
      onClose={close}
      eyebrow="Bank Accounts"
      title={isEditMode ? "Edit Bank Account" : "Add Bank Account"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            onClick={close}
            className="ui-btn-cancel min-w-[108px]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid}
            className="ui-btn-save min-w-[108px]"
          >
            {isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Account details</h4>
        <div className="space-y-4">
          <div>
            <label className="ui-form-label">
              Bank Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="bankName"
              value={formData.bankName}
              onChange={handleChange}
              placeholder="Enter bank name"
              className={fieldClass("bankName")}
            />
            {errors.bankName ? (
              <p className="ui-form-error">{errors.bankName}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Minimum 2 characters, alphabets and spaces only
              </p>
            )}
            <UrduInput
              label="بینک کا نام"
              name="bankName_ur"
              value={formData.bankName_ur}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="ui-form-label">
              Account Holder Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="accountHolderName"
              value={formData.accountHolderName}
              onChange={handleChange}
              placeholder="Enter account holder name"
              className={fieldClass("accountHolderName")}
            />
            {errors.accountHolderName ? (
              <p className="ui-form-error">{errors.accountHolderName}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Minimum 2 characters, alphabets and spaces only
              </p>
            )}
            <UrduInput
              label="اکاؤنٹ ہولڈر کا نام"
              name="accountHolderName_ur"
              value={formData.accountHolderName_ur}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="ui-form-label">
              Account Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
              placeholder="Enter account number"
              className={fieldClass("accountNumber")}
            />
            {errors.accountNumber ? (
              <p className="ui-form-error">{errors.accountNumber}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Minimum 5 digits, numbers only
              </p>
            )}
          </div>

          <div>
            <label className="ui-form-label">
              IBAN <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="iban"
              value={formData.iban}
              onChange={handleChange}
              placeholder="Enter IBAN"
              className={fieldClass("iban")}
            />
            {errors.iban ? (
              <p className="ui-form-error">{errors.iban}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Minimum 8 characters, letters and numbers only (no special
                characters)
              </p>
            )}
          </div>

          <div>
            <label className="ui-form-label">
              Branch Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="branchCode"
              value={formData.branchCode}
              onChange={handleChange}
              placeholder="Enter branch code"
              className={fieldClass("branchCode")}
            />
            {errors.branchCode ? (
              <p className="ui-form-error">{errors.branchCode}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Minimum 3 digits, numbers only
              </p>
            )}
          </div>
        </div>
      </section>
    </UiModal>
  );
};

export default ModalBankAccount;
